<template>
  <FormInput
    ref="field"
    v-bind="_props"
    v-on="$listeners"
    @focus="onFocus"
    @blur="onBlur"
    @update:value="onValueChange"
    class="place-autocomplete-input"
  />
</template>

<script>
/**
 * This component wraps Google Maps Places Autocomplete Widget.
 * @see: {@link https://developers.google.com/maps/documentation/javascript/reference/places-widget}
 *
 */

import {
  notifyExternalServices,
  ACTIONS,
} from '@/services/external-communication/external-communication'
import { isEmpty } from '@/helpers/string'
import { addScriptToDOM, isScriptAddedToDOM } from '@/helpers/script'
import { createAttributeMutationObserver } from '@/helpers/observer-mutation'
import { getGoogleMapsPlaceUrl, GOOGLE_MAPS_PLACE_URL_REG_EXP } from '@/helpers/google-maps'
import { findIntersection } from '@/helpers/string'
import { toRetry } from '@/helpers/request'
import formFieldInterfaceMixin from '@/components/_shared/Form/form-field-interface-mixin'
import FormInput from '@/components/_shared/Form/Input/FormInput.vue'
import propsInput from '@/components/_shared/Form/Input/props-input'

// restrictions only allow for up to 5 countries (see: https://developers.google.com/maps/documentation/javascript/reference/places-autocomplete-service#ComponentRestrictions.country)
export const RESTRICTED_COUNTRIES_MAX = 5

// p is from place response
const PLACE_MAPPING = {
  address1: place => {
    const street = getComponent(place, 'route', 'long_name') || ''
    const streetNumber = getComponent(place, 'street_number', 'short_name') || ''
    const apartmentNumber = getComponent(place, 'subpremise', 'long_name') || ''

    let address = street

    if (street && streetNumber) {
      address = startsWithNumber(streetNumber, place.formatted_address)
        ? `${streetNumber} ${street}`
        : `${street} ${streetNumber}`
    }

    return `${address} ${apartmentNumber}`.trim()
  },
  address2: place => (place.types?.includes('establishment') ? place.name : ''),
  neighborhood: place => getComponent(place, 'sublocality', 'long_name'),
  city: place =>
    getComponent(place, 'locality', 'long_name') ||
    getComponent(place, 'administrative_area_level_2', 'long_name'),
  state: place => getComponent(place, 'administrative_area_level_1', 'long_name'),
  country: place => getComponent(place, 'country', 'long_name'),
  countryIso: place => getComponent(place, 'country', 'short_name').toLowerCase(),
  zipcode: place => getComponent(place, 'postal_code', 'short_name'),
}

export const getAutocompletedFields = () => Object.keys(PLACE_MAPPING)

const getComponent = ({ address_components: components }, type, prop) => {
  const component = components.find(c => c.types.find(t => t === type))
  return component ? component[prop] : ''
}

const startsWithNumber = (number, address) => findIntersection(number, address).position == 0

/**
 * Place fields to be included in response when the details are successfully retrieved.
 * Avoids paying for data that is not needed by restricting the set fields fetched from Place API.
 * @see: for options {@link https://developers.google.com/maps/documentation/javascript/reference/places-service#PlaceResult}
 */
const PLACE_RESPONSE_FIELDS = [
  'geometry',
  'types',
  'address_component',
  'name',
  'formatted_address',
]

export default {
  name: 'PlaceAutocompleteInput',
  components: {
    FormInput,
  },
  mixins: [formFieldInterfaceMixin],

  props: {
    ...propsInput,

    language: {
      type: String,
      default: '',
    },
    /**
     * Restricts predictions to the specified country (ISO 3166-1 Alpha-2 country code, case insensitive). For example, 'us', 'br', or 'au'.
     * @see: {@link https://developers.google.com/maps/documentation/javascript/reference/places-autocomplete-service#ComponentRestrictions}
     */
    restrictedCountries: {
      type: Array,
      default: () => [],
      validator(array) {
        return array.length <= RESTRICTED_COUNTRIES_MAX
      },
    },
    /**
     * The types of predictions to be returned.
     * Keep empty for all types.
     * @see: for type options {@link https://developers.google.com/places/supported_types#table3}
     */
    placeTypes: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      placesAutocomplete: null,
      isLibraryAddedToDOMByComponent: false,
    }
  },

  watch: {
    // FIX: using google places widgets breaks the vue watcher, so we need to update by hand
    value: 'setValue',
  },

  mounted() {
    this.updateAutofill(this.value)
    if (!this.isScriptAddedToDOM()) {
      this.addScriptToDOM()
    } else if (!this.isLibraryAddedToDOMByComponent) {
      this.initAutocomplete()
    }
  },

  methods: {
    updateAutofill(value) {
      const isEnabled = isEmpty(value)
      this.toggleAutofill(isEnabled)
    },

    toggleAutofill(isEnabled) {
      const toggleAutofillHelper = (isEnabled = false) => {
        if (isEnabled) this.getFieldEl()?.removeAttribute('autocomplete')
        else this.getFieldEl()?.setAttribute('autocomplete', this.id)
      }
      toggleAutofillHelper(isEnabled)
      // google.maps.places.Autocomplete is changing autocomplte attribute and we will change it back
      const observer = createAttributeMutationObserver(
        this.getFieldEl(),
        'autocomplete',
        () => toggleAutofillHelper(isEnabled),
        { observeOnce: true }
      )
      observer.observe()
    },

    setValue(value) {
      const inputEl = this.getFieldEl()
      if (inputEl) inputEl.value = value || ''
    },

    isScriptAddedToDOM() {
      return isScriptAddedToDOM(GOOGLE_MAPS_PLACE_URL_REG_EXP)
    },

    addScriptToDOM() {
      window.initPlaceAutocompleteInput = this.initAutocomplete
      const src = getGoogleMapsPlaceUrl('window.initPlaceAutocompleteInput', this.language)
      toRetry(() => addScriptToDOM(src))
      this.isLibraryAddedToDOMByComponent = true
    },

    initAutocomplete() {
      const options = {}
      options.types = this.placeTypes
      if (this.restrictedCountries.length) {
        options.componentRestrictions = {
          country: this.restrictedCountries,
        }
      }
      this.placesAutocomplete = new google.maps.places.Autocomplete(this.getFieldEl(), options)
      this.placesAutocomplete.setFields(PLACE_RESPONSE_FIELDS)
      this.placesAutocomplete.addListener('place_changed', this.onPlaceChanged)
    },

    onValueChange(value) {
      this.updateAutofill(value)
    },

    onFocus() {
      this.updateAutofill(this.value)
    },

    onBlur() {
      this.updateAutofill(this.value)
    },

    onPlaceChanged() {
      const place = this.placesAutocomplete.getPlace()
      if (!place.geometry) {
        // User entered the name of a Place that was not suggested and
        // pressed the Enter key, or the Place Details request failed.
        return notifyExternalServices({
          action: ACTIONS.errors,
          details: {
            action: 'checkout_address',
            label: 'autocomplete',
          },
        })
      }
      if (place.address_components !== undefined) {
        const formattedPlace = this.mapData(place, PLACE_MAPPING)
        this.$emit('placechanged', formattedPlace)
        this.setValue(formattedPlace[this.id])
      }
    },

    /**
     * Maps data using mapping functions.
     * Only keys where a mapping function is avaliable will be mapped and returned.
     *
     * @param {Object} place - All the information that is included in the response
     * @param {Object} mappingFunctions - Object of mapping functions to be used for mapping stored by data key
     *
     * @returns {Object} Mapped data stored by mapping key
     */
    mapData(place, mappingFunctions) {
      return Object.keys(mappingFunctions).reduce((result, key) => {
        result[key] = mappingFunctions[key](place) || ''
        return result
      }, {})
    },
  },
}
</script>

<style lang="scss">
$background-color: $color-white;

.pac-container {
  box-sizing: content-box; // increase width to match input width using border
  border: solid 1px $color-grey-light-5;
  background-color: $background-color;
  box-shadow: 2px 2px 8px rgba(0, 0, 0, 0.1);
  border-radius: 0px;
  margin-top: -1px;
}

.pac-item {
  padding: 0 space(4);
  font-family: $font-family-body;
  font-size: $font-size-small;
  line-height: space(13, $font-size-small);
  letter-spacing: $font-spacing-small;
  color: $color-grey-mid-2;
  border: none;

  &-query {
    font-size: $font-size-small;
    color: $color-black;
  }

  &:hover,
  &-selected {
    background-color: $color-grey-light-4;
  }
}

.pac-icon {
  display: none;
}

.pac-logo:after,
.hdpi.pac-logo:after {
  height: 26px;
  margin-right: 10px;
  background-image: url('~@/assets/images/google/powered_by_google_on_non_white.png');
  background-size: 120px 15px;
}
</style>
